// Ensayo del telefono (video aprobado "Audio listo antes de llamar"): logica pura de la prueba de audio y conexion.
// Pasos en orden: navegador, microfono, altavoces y conexion con la central. La camara no se usa (solo informativa).
// Aqui no hay React ni dispositivos: estado de los pasos, nivel del microfono, latencia y el tono de prueba.

export type PasoId = 'nav' | 'mic' | 'alt' | 'con';
export type EstadoPaso = 'pend' | 'run' | 'ok' | 'falla';
export type Fila = PasoId | 'cam';

export const PASOS: { id: PasoId; titulo: string; ok: string; ayuda: string }[] = [
  { id: 'nav', titulo: 'Navegador', ok: 'Llamadas por internet disponibles', ayuda: 'Revisamos que tu navegador pueda hacer llamadas cifradas por internet.' },
  { id: 'mic', titulo: 'Micrófono', ok: 'Te escuchamos bien', ayuda: 'Habla y mira la barra: debe moverse con tu voz.' },
  { id: 'alt', titulo: 'Altavoces', ok: 'Se oye bien', ayuda: 'Toca «Reproducir tono». ¿Lo escuchas?' },
  { id: 'con', titulo: 'Conexión con la central', ok: 'Estable', ayuda: 'Medimos cuánto tarda en responder la central.' },
];

export type Ensayo = {
  pasos: Record<PasoId, EstadoPaso>;
  detalle: Partial<Record<PasoId, string>>;
  abierto: Fila | null;
  // Sube con cada reintento: sirve para repetir el paso aunque su estado no cambie.
  intento: number;
};

export type AccionEnsayo =
  | { tipo: 'inicia'; paso: PasoId }
  | { tipo: 'ok'; paso: PasoId; detalle?: string }
  | { tipo: 'falla'; paso: PasoId; detalle: string }
  | { tipo: 'reintenta'; paso: PasoId }
  | { tipo: 'abre'; fila: Fila | null }
  | { tipo: 'reinicia' };

export const ensayoInicial = (): Ensayo => ({ pasos: { nav: 'pend', mic: 'pend', alt: 'pend', con: 'pend' }, detalle: {}, abierto: null, intento: 0 });

export function reducirEnsayo(e: Ensayo, a: AccionEnsayo): Ensayo {
  switch (a.tipo) {
    case 'inicia':
      if (e.pasos[a.paso] !== 'pend') return e;
      return { ...e, pasos: { ...e.pasos, [a.paso]: 'run' }, abierto: a.paso };
    case 'ok':
      if (e.pasos[a.paso] !== 'run') return e;
      return { ...e, pasos: { ...e.pasos, [a.paso]: 'ok' }, detalle: { ...e.detalle, [a.paso]: a.detalle }, abierto: e.abierto === a.paso ? null : e.abierto };
    case 'falla':
      if (e.pasos[a.paso] !== 'run') return e;
      // Queda abierto para que se vea que paso y como arreglarlo.
      return { ...e, pasos: { ...e.pasos, [a.paso]: 'falla' }, detalle: { ...e.detalle, [a.paso]: a.detalle }, abierto: a.paso };
    case 'reintenta':
      if (e.pasos[a.paso] === 'run') return e;
      return { ...e, pasos: { ...e.pasos, [a.paso]: 'pend' }, detalle: { ...e.detalle, [a.paso]: undefined }, intento: e.intento + 1 };
    case 'abre':
      return { ...e, abierto: e.abierto === a.fila ? null : a.fila };
    case 'reinicia':
      return { ...ensayoInicial(), intento: e.intento + 1 };
  }
}

export const pasoEnCurso = (e: Ensayo): PasoId | null => PASOS.find((p) => e.pasos[p.id] === 'run')?.id ?? null;

// El siguiente paso que toca correr: ninguno mientras otro esta corriendo. Un paso que fallo no frena a los demas.
export const siguientePaso = (e: Ensayo): PasoId | null => (pasoEnCurso(e) ? null : (PASOS.find((p) => e.pasos[p.id] === 'pend')?.id ?? null));

export const cuentaHechos = (e: Ensayo) => PASOS.filter((p) => e.pasos[p.id] === 'ok').length;
export const estaListo = (e: Ensayo) => cuentaHechos(e) === PASOS.length;
export const hayFallas = (e: Ensayo) => PASOS.some((p) => e.pasos[p.id] === 'falla');

const limita = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

// Nivel del microfono (0 a 1) desde las muestras de un AnalyserNode (getByteTimeDomainData: 128 es silencio).
// Se pasa a decibelios para que la barra se mueva con la voz normal y no solo con gritos: -50 dB = 0, 0 dB = 1.
export function nivelDeMuestras(datos: ArrayLike<number>): number {
  if (!datos.length) return 0;
  let suma = 0;
  for (let i = 0; i < datos.length; i++) {
    const v = (datos[i] - 128) / 128;
    suma += v * v;
  }
  const rms = Math.sqrt(suma / datos.length);
  if (rms <= 0) return 0;
  return limita((20 * Math.log10(rms) + 50) / 50);
}

export const barrasEncendidas = (nivel: number, total: number) => Math.round(limita(nivel) * total);

// Suaviza el nivel: sube rapido y baja despacio, como un vumetro.
export const suavizarNivel = (anterior: number, nuevo: number) => (nuevo > anterior ? nuevo : anterior * 0.85 + nuevo * 0.15);

// Umbral desde el que consideramos que el microfono "te escucha".
export const UMBRAL_VOZ = 0.2;

export type EntornoNavegador = { seguro: boolean; rtc: boolean; microfono: boolean; audio: boolean };

export function revisarNavegador(env: EntornoNavegador): { ok: boolean; faltan: string[] } {
  const faltan: string[] = [];
  if (!env.seguro) faltan.push('Abre la Consola con https:// (conexión segura)');
  if (!env.rtc) faltan.push('Tu navegador no permite llamadas por internet (WebRTC)');
  if (!env.microfono) faltan.push('Tu navegador no deja usar el micrófono');
  if (!env.audio) faltan.push('Tu navegador no puede reproducir audio de llamadas');
  return { ok: !faltan.length, faltan };
}

export function calificarLatencia(ms: number): { texto: string; lenta: boolean } {
  const r = Math.round(ms);
  if (r < 150) return { texto: `Estable · ${r} ms`, lenta: false };
  if (r < 400) return { texto: `Aceptable · ${r} ms`, lenta: false };
  return { texto: `Lenta · ${r} ms`, lenta: true };
}

// Si el dispositivo guardado ya no esta conectado, se usa el predeterminado ('').
export const dispositivoValido = (lista: { deviceId: string }[], id: string | undefined) => (id && lista.some((d) => d.deviceId === id) ? id : '');

// Texto para la persona segun el error de getUserMedia.
export function mensajeErrorMedios(err: unknown): string {
  const n = (err as { name?: string })?.name ?? '';
  if (n === 'NotAllowedError' || n === 'SecurityError') return 'Sin permiso para el micrófono. Permítelo en el candado de la barra de direcciones y vuelve a intentar.';
  if (n === 'NotFoundError' || n === 'OverconstrainedError') return 'No encontramos ese micrófono. Conéctalo o elige otro.';
  if (n === 'NotReadableError' || n === 'AbortError') return 'Otro programa está usando el micrófono. Ciérralo y vuelve a intentar.';
  return 'No pudimos abrir el micrófono.';
}

// Tono de prueba: WAV PCM de 8 bits, mono, con entrada y salida suaves para que no "chasquee".
export function wavTono(frecuencia = 660, ms = 900, muestreo = 16000): Uint8Array<ArrayBuffer> {
  const n = Math.round((muestreo * ms) / 1000);
  const w = new Uint8Array(44 + n);
  const dv = new DataView(w.buffer);
  const txt = (o: number, s: string) => [...s].forEach((c, i) => (w[o + i] = c.charCodeAt(0)));
  txt(0, 'RIFF');
  dv.setUint32(4, 36 + n, true);
  txt(8, 'WAVE');
  txt(12, 'fmt ');
  dv.setUint32(16, 16, true);
  dv.setUint16(20, 1, true);
  dv.setUint16(22, 1, true);
  dv.setUint32(24, muestreo, true);
  dv.setUint32(28, muestreo, true);
  dv.setUint16(32, 1, true);
  dv.setUint16(34, 8, true);
  txt(36, 'data');
  dv.setUint32(40, n, true);
  const rampa = Math.max(1, Math.round(muestreo * 0.04));
  for (let i = 0; i < n; i++) {
    const env = Math.min(1, i / rampa, (n - 1 - i) / rampa);
    w[44 + i] = Math.round(128 + 70 * env * Math.sin((2 * Math.PI * frecuencia * i) / muestreo));
  }
  return w;
}

type SocketMinimo = { onopen: (() => void) | null; onerror: (() => void) | null; close: () => void };

// Mide cuanto tarda en abrirse el WebSocket de la pasarela de voz (conexion + saludo). Rechaza si no responde a tiempo.
// Devuelve tambien una funcion para cancelar (al cerrar la prueba).
export function medirCentral(
  url: string,
  { espera = 5000, crear = (u: string) => new WebSocket(u, 'sip') as unknown as SocketMinimo, ahora = () => performance.now() } = {},
): { promesa: Promise<number>; cancelar: () => void } {
  let ws: SocketMinimo | null = null;
  let reloj: ReturnType<typeof setTimeout> | undefined;
  let cancelar = () => {};
  const promesa = new Promise<number>((resolve, reject) => {
    const cierra = () => {
      clearTimeout(reloj);
      if (ws) {
        ws.onopen = ws.onerror = null;
        try {
          ws.close();
        } catch {
          /* ya cerrado */
        }
      }
      ws = null;
    };
    cancelar = () => {
      cierra();
      reject(new Error('cancelada'));
    };
    const t0 = ahora();
    try {
      ws = crear(url);
    } catch {
      return reject(new Error('La dirección de la pasarela no es válida.'));
    }
    reloj = setTimeout(() => {
      cierra();
      reject(new Error('La central no respondió a tiempo.'));
    }, espera);
    ws.onopen = () => {
      const ms = ahora() - t0;
      cierra();
      resolve(ms);
    };
    ws.onerror = () => {
      cierra();
      reject(new Error('No pudimos conectar con la pasarela de voz.'));
    };
  });
  return { promesa, cancelar };
}
